import { $BlockchainsEnum } from "./enum.dto";
import {
  faFilePdf,
  faFileSignature,
  faFilePowerpoint,
  faFileCsv,
  faFileAudio,
  faFileLines,
  faFileVideo,
  faIcons,
  faImage,
} from "@fortawesome/free-solid-svg-icons";

export const getTrimFilename = (name: string) => {
  let type = name?.split(".")[1];
  let start = name?.split("")?.slice(0, 4)?.join("");
  let end = name
    ?.split(".")[0]
    ?.split("")
    ?.reverse()
    ?.slice(0, 4)
    ?.reverse()
    ?.join("");
  return `${start}***${end}.${type}`;
};

export const getTrimHash = (hash: string, nr: number = 4) => {
  let start = hash?.split("")?.slice(0, nr)?.join("");
  let end = hash?.split("")?.reverse()?.slice(0, nr)?.reverse()?.join("");
  return `${start}...${end}`;
};

export const getShortString = (
  string: string,
  number: number = 20,
  cut = "end"
) => {
  let newString = string;
  switch (cut) {
    case "end":
      newString = string.slice(0, number);
      return `${newString}...`;
    case "middle":
      const start = string.slice(0, Math.floor(number / 2));
      const end = string.slice(-Math.ceil(number / 2));
      return `${start}...${end}`;
    default:
      newString = string.slice(0, number);
      return `...${newString}`;
  }
};

export const getCurrentUrl = () => {
  return window.location.href.split("/?")[0];
};

// Custom event name used to signal an in-app navigation. App.tsx listens to it
// (alongside the native `popstate`) and re-syncs state from the URL without a
// full page reload.
export const NAV_EVENT = "tx-explorer:navigation";

// SPA navigation: push a new URL into history then notify listeners. Use this
// instead of `window.location.href = url` so the page does not reload.
export const navigate = (url: string) => {
  if (url !== window.location.href) {
    window.history.pushState({}, "", url);
  }
  window.dispatchEvent(new CustomEvent(NAV_EVENT));
};

// Notarify stores TON ids as `<wallet>/<lt>_<hex hash>`, Blackbox as a base64
// hash; tonviewer takes the hex hash alone.
const tonHash = (transaction: string) => {
  const tail = transaction.split("_").pop() ?? transaction;
  if (/^[0-9a-fA-F]{64}$/.test(tail)) return tail.toLowerCase();
  try {
    return Array.from(
      atob(transaction.replace(/-/g, "+").replace(/_/g, "/")),
      (c) => c.charCodeAt(0).toString(16).padStart(2, "0")
    ).join("");
  } catch {
    return transaction;
  }
};

export const getExplorerRef = (transaction: string, blockchain: string) => {
  switch (blockchain) {
    case $BlockchainsEnum.EOS:
      return `https://bloks.io/transaction/${transaction}`;
    case $BlockchainsEnum.Bitcoin:
      return `https://live.blockcypher.com/btc/tx/${transaction}`;
    case $BlockchainsEnum.Ethereum:
      return `https://etherscan.io/tx/${transaction}`;
    case $BlockchainsEnum.Solana:
      return `https://explorer.solana.com/tx/${transaction}`;
    case $BlockchainsEnum.Ton:
      return `https://tonviewer.com/transaction/${tonHash(transaction)}`;
    default:
      return `https://live.blockcypher.com/btc/tx/${transaction}`;
  }
};

export const getDocumentTypeIcon: any = (key: string) => {
  switch (key) {
    case "pdf":
      return faFilePdf;
    case "mp3":
      return faFileAudio;
    case "wav":
      return faFileAudio;
    case "mp4":
      return faFileVideo;
    case "webm":
      return faFileVideo;
    case "svg":
      return faIcons;
    case "csv":
      return faFileCsv;
    case "pptx":
      return faFilePowerpoint;
    case "ppt":
      return faFilePowerpoint;
    case "jpg":
      return faImage;
    case "jpeg":
      return faImage;
    case "png":
      return faImage;
    default:
      return faFileLines;
  }
};

export function checkHashFormat(
  hash: string,
  algorithm: "sha256" | "md5" = "sha256"
) {
  // Regular expression for a valid SHA-256 hash
  const regexConfig = {
    sha256: /^[a-fA-F0-9]{64}$/,
    md5: /^[a-fA-F0-9]{32}$/,
  };

  // Test if the provided hash matches the SHA-256 regex
  return regexConfig[algorithm].test(hash);
}

export const getChainName = (name: string) => {
  switch (name.toUpperCase()) {
    case 'EOS':
      return $BlockchainsEnum.EOS;
    case 'BTC':
      return $BlockchainsEnum.Bitcoin;
    case 'ETH':
      return $BlockchainsEnum.Ethereum;
    case 'SOL':
      return $BlockchainsEnum.Solana;
    case 'TON':
      return $BlockchainsEnum.Ton;
    default:
      return ;
  }
}

export function addZero(number: number) {
  return number < 10 ? '0' + number : number;
}

function capitalize(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/\w\S*/g, w => w.replace(/^\w/, c => c.toUpperCase()));
}

// Naive ISO strings (no TZ designator) must be read as UTC, otherwise new Date()
// treats them as local time. Document/block datetimes arrive as epoch ms.
function toDate(input: Date | string | number): Date {
  if (input instanceof Date) return input;
  if (
    typeof input === 'string' &&
    /\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(input) &&
    !/(Z|[+-]\d{2}:?\d{2})$/.test(input)
  ) {
    return new Date(`${input}Z`);
  }
  return new Date(input);
}

// Match NotariDrive (utils/date.ts): viewer-local timezone, locale-aware, no tz label.
export function getFullDate(input: Date | string | number | undefined) {
  if (input === undefined || input === null) return '';
  const date = toDate(input);
  if (Number.isNaN(date.getTime())) return '';
  const locale =
    typeof navigator !== 'undefined' && navigator.language
      ? navigator.language
      : undefined;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const datePart = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    day: 'numeric',
    month: 'short',
    timeZone,
  }).format(date);
  const timePart = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: 'numeric',
    timeZone,
  }).format(date);
  return capitalize(`${datePart}, ${timePart}`);
}